import styled from "@emotion/styled";
import { SelectTokenRowTooltip } from "components/TokenSelector/elements/SelectTokenRowTooltip";
import { SkeletonBlock } from "components/general/Skeleton";
import { Text } from "components/general/Text";
import { useGetTokenRowDisabledReason } from "hooks/trade/useGetTokenRowDisabledReason";
import React, { CSSProperties, useCallback, useMemo } from "react";
import { GlobalStore } from "store/global/GlobalStore";
import { TokenList } from "store/global/getAllTokenLists";
import { Amount } from "types/amount";
import { CounterParty } from "utils/tradeUtils";
import { addTokenToStore, changeTokenOnStore } from "../../../utils/tokenSelectorUtils";
import { Hash } from "viem";
import { getTokenTicker, isAddressEqual } from "../../../utils/tokenUtils";
import { TokenRowIcon } from "./TokenRowIcon";
import { TokenRowStats } from "./TokenRowStats";
import { TokenListMetadata } from "../../../hooks/queries/tokens-queries";

interface TokenSelectorRowProps {
  tokenAddress: Hash;
  counterParty: CounterParty;
  showSeparatorAbove: boolean;
  closeDropdown: () => void;
  scrollToTop: () => void;
  isFirst: boolean;
  isLastHighlightedWithKeyboard: boolean;
  style?: CSSProperties;
  isLoading?: boolean;
  isHighlighted?: boolean;
  isChangingToken: Hash | undefined;
  tokenList: TokenList | undefined;
  setHighlightedRowIdx: React.Dispatch<React.SetStateAction<number | null>>;
  handleDiagonalMouseMove: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  handleMouseEnterRow: () => void;
  tokenListMetadata: TokenListMetadata | undefined;
}

export const TokenSelectorRow = ({
  tokenAddress,
  counterParty,
  showSeparatorAbove,
  closeDropdown,
  scrollToTop,
  isFirst,
  isLastHighlightedWithKeyboard,
  style,
  isLoading = false,
  isHighlighted = false,
  isChangingToken,
  tokenList,
  handleDiagonalMouseMove,
  handleMouseEnterRow,
  tokenListMetadata,
}: TokenSelectorRowProps) => {
  const balances = GlobalStore.useState(s => s.balances);
  const balance = balances.find(entry => isAddressEqual(entry.tokenAddress, tokenAddress))?.balance;
  const displayBalance = useMemo(
    () => getDisplayBalance(balance, tokenAddress, tokenList),
    [balance, tokenAddress, tokenList]
  );
  const tokenPrice = new Amount(tokenListMetadata?.[tokenAddress].p?.toString() ?? "0");
  const balanceUsd = balance ? tokenPrice.value.mul(balance.value) : Amount.ZERO.value;
  const theme = GlobalStore.useState(s => s.theme);
  const tokenRowDisabledReason = useGetTokenRowDisabledReason()(
    tokenAddress,
    counterParty,
    isChangingToken
  );
  const isDisabled = tokenRowDisabledReason != null;

  if (isHighlighted && isLastHighlightedWithKeyboard) {
    if (isFirst) {
      scrollToTop();
    }
  }
  const MainDetails = useCallback(() => {
    const TokenName = () => {
      if (isLoading) {
        return (
          <div style={{ marginBottom: 3 }}>
            <SkeletonBlock width={94} height={14} />
          </div>
        );
      }
      return (
        <Text as="p" textStyle="Title" size="Small" shouldScale={true}>
          {tokenList?.[tokenAddress]?.name}
        </Text>
      );
    };

    const BalanceDetails = () => {
      if (isLoading) {
        return <SkeletonBlock width={54} height={12} />;
      }

      if (balance != null && balance.value.greaterThan(0)) {
        return (
          <BalanceDetailsContainer>
            <Text textStyle="Paragraph" size="Large" shouldScale={true}>
              {displayBalance}
            </Text>
          </BalanceDetailsContainer>
        );
      }

      return (
        <BalanceDetailsContainer>{getTokenTicker(tokenAddress, tokenList)}</BalanceDetailsContainer>
      );
    };

    return (
      <SubContainer>
        <TokenName />
        <BalanceDetails />
      </SubContainer>
    );
  }, [tokenList, tokenAddress, balance, isLoading, displayBalance]);

  if (tokenList != null && !(tokenAddress in tokenList)) {
    return null;
  }

  const content = (
    <SelectTokenRowTooltip
      tokenDisabledReason={tokenRowDisabledReason}
      counterParty={counterParty}
      tokenAddress={tokenAddress}
    >
      <Container
        onMouseMove={e => {
          handleDiagonalMouseMove(e);
        }}
        onMouseEnter={() => handleMouseEnterRow()}
        style={showSeparatorAbove ? undefined : style}
        isLoading={isLoading}
        isHighlighted={isHighlighted}
        isDisabled={isDisabled}
        onClick={() => {
          if (isDisabled) return;
          if (isChangingToken) {
            void changeTokenOnStore(counterParty, isChangingToken, tokenAddress);
          } else {
            addTokenToStore(counterParty, tokenAddress);
          }
          closeDropdown();
        }}
      >
        <TokenRowIcon tokenAddress={tokenAddress} isLoading={isLoading} />
        <MainDetails />
        <TokenRowStats tokenAddress={tokenAddress} isLoading={isLoading} balanceUsd={balanceUsd} />
      </Container>
    </SelectTokenRowTooltip>
  );

  if (showSeparatorAbove) {
    return (
      <div style={{ ...style, width: "100%" }}>
        {showSeparatorAbove && (
          <div
            style={{
              marginTop: 13,
              marginBottom: 13,
              height: 1,
              width: "100%",
              backgroundColor: theme.button.secondary.text.active,
            }}
          />
        )}
        {content}
      </div>
    );
  }

  return <>{content}</>;
};

// Sub-components

const Container = styled.button<{
  isLoading: boolean;
  isHighlighted: boolean;
  isDisabled: boolean;
}>`
  display: flex;
  padding: 12px 8px 12px 12px;
  border-radius: 18px;
  align-items: center;
  background: ${props => (props.isHighlighted ? props.theme.surface.main.secondary.hover : "none")};
  border: none;
  transition: background 0.1s linear;
  color: ${props => props.theme.font.primary.default};
  text-align: left;
  opacity: ${props => (props.isDisabled ? 0.5 : 1)};
  cursor: ${props =>
    props.isLoading || props.isDisabled ? "not-allowed!important" : "pointer!important"};
  width: 100%;

  &:hover {
    background: ${props =>
      !props.isLoading ? props.theme.surface.main.secondary.hover : "default"};
  }
`;

const SubContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex-grow: 1;
  margin-left: 7px;
`;

const BalanceDetailsContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  color: ${props => props.theme.font.secondary.default};
`;

// Helpers

function getDisplayBalance(
  amount: Amount | undefined,
  tokenAddress: Hash,
  tokenList: TokenList | undefined
) {
  if (amount == null || amount.value.eq("0")) {
    return "";
  }
  return `${amount.toDisplay(tokenAddress, tokenList)} ${getTokenTicker(tokenAddress, tokenList)}`;
}
